/* GrowthOS — shared Email-Template design primitives (used by both the Templates
   list page `templates.jsx` and the Template Gallery `templateGallery.jsx`).
   Ports the design's Icon set, type metadata + resolver, CSS email-preview thumbnail,
   and the type/module chips + avatar. Template `type` is admin-configurable, so
   resolveType() maps the design's fixed TYPES names first, then keyword-matches,
   then hashes → a known type meta, so any custom type still gets a stable color+layout. */
import React, { useEffect, useRef } from 'react';
import { Ic } from "../../../shared/component/GosIcon";
export { Ic };


export const TYPES = {
  'Newsletter': { color: '#2e63a6', tint: '#e9f1fc', layout: 'newsletter', desc: 'Regular updates & digests' },
  'AI Agent': { color: '#6a4fae', tint: '#efe9fb', layout: 'agent', desc: 'Automated agent replies' },
  'Claims': { color: '#0e8c6f', tint: '#e3f3ee', layout: 'notice', desc: 'Claims & case notices' },
  'BPO': { color: '#c0473f', tint: '#fdeaea', layout: 'notice', desc: 'Back-office operations' },
  'IT Services': { color: '#2c7a45', tint: '#e2f4e8', layout: 'promo', desc: 'Technical service comms' },
  'Basic': { color: '#6b6353', tint: '#efede6', layout: 'basic', desc: 'Plain starting point' },
  'Product Promotion': { color: '#b34480', tint: '#fbe7f1', layout: 'promo', desc: 'Offers & product pushes' },
  'Follow-up': { color: '#9a751c', tint: '#fbf0d8', layout: 'basic', desc: 'Nudges & reminders' },
  'Invitation': { color: '#7c4ddb', tint: '#efe9fb', layout: 'invite', desc: 'Events & webinars' },
  'AS400': { color: '#4a72a8', tint: '#e9f1fc', layout: 'basic', desc: 'Legacy system reports' },
};
const TYPE_LIST = Object.values(TYPES);
export const resolveType = (t) => {
  if (t && TYPES[t]) return TYPES[t];
  const s = String(t || '').toLowerCase();
  if (s.includes('news')) return TYPES['Newsletter'];
  if (s.includes('agent') || s.includes(' ai') || s.startsWith('ai')) return TYPES['AI Agent'];
  if (s.includes('claim')) return TYPES['Claims'];
  if (s.includes('bpo')) return TYPES['BPO'];
  if (s.includes('it ') || s.includes('service')) return TYPES['IT Services'];
  if (s.includes('promo') || s.includes('product') || s.includes('offer')) return TYPES['Product Promotion'];
  if (s.includes('follow') || s.includes('remind')) return TYPES['Follow-up'];
  if (s.includes('invit') || s.includes('event') || s.includes('webinar')) return TYPES['Invitation'];
  if (s.includes('as400') || s.includes('legacy')) return TYPES['AS400'];
  if (s.includes('basic') || s.includes('plain')) return TYPES['Basic'];
  if (!s) return TYPES['Basic'];
  const h = s.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return TYPE_LIST[h % TYPE_LIST.length];
};

const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
export const initials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
export const avaColor = (name) =>
  AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length];
export const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
export const modIcon = (m) => (m === 'Lead' ? 'user' : m === 'Contact' ? 'contacts' : 'building');

export const useOutside = (open, onClose) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, onClose]);
  return ref;
};

/* CSS email preview thumbnail (per resolved layout) */
export const Preview = ({ type, compact }) => {
  const meta = resolveType(type);
  const c = meta.color, layout = meta.layout;
  const line = (w, faint, i) => <span key={i} className={'gos-tpl-pv-line' + (faint ? ' faint' : '')} style={{ width: w }} />;
  let inner;
  if (layout === 'newsletter') {
    inner = [
      <div key="b" className="gos-tpl-pv-band" style={{ background: c }}><span className="gos-tpl-pv-logo" /></div>,
      <div key="h" className="gos-tpl-pv-hero" style={{ background: meta.tint }} />,
      <div key="t" className="gos-tpl-pv-body">{[line('90%', 0, 1), line('100%', 1, 2), line('96%', 1, 3), line('60%', 1, 4)]}</div>,
      <div key="g" className="gos-tpl-pv-2col"><span style={{ background: meta.tint }} /><span style={{ background: meta.tint }} /></div>,
    ];
  } else if (layout === 'promo') {
    inner = [
      <div key="h" className="gos-tpl-pv-hero tall" style={{ background: c }}><span className="gos-tpl-pv-hero-txt" /></div>,
      <div key="t" className="gos-tpl-pv-body center">{[line('70%', 0, 1), line('88%', 1, 2), line('50%', 1, 3)]}</div>,
      <div key="c" className="gos-tpl-pv-cta" style={{ background: c }} />,
    ];
  } else if (layout === 'agent') {
    inner = [
      <div key="b" className="gos-tpl-pv-band sm" style={{ background: c }} />,
      <div key="t" className="gos-tpl-pv-body">{[line('40%', 1, 1), line('100%', 0, 2), line('94%', 1, 3), line('98%', 1, 4), line('72%', 1, 5)]}</div>,
      <div key="s" className="gos-tpl-pv-sign"><span className="gos-tpl-pv-ava" style={{ background: c }} /><span className="gos-tpl-pv-sign-lines">{[line('60px', 0, 1), line('42px', 1, 2)]}</span></div>,
    ];
  } else if (layout === 'notice') {
    inner = [
      <div key="b" className="gos-tpl-pv-band" style={{ background: c }}><span className="gos-tpl-pv-logo" /></div>,
      <div key="n" className="gos-tpl-pv-notice" style={{ borderColor: c, background: meta.tint }}>{[line('80%', 0, 1), line('55%', 1, 2)]}</div>,
      <div key="t" className="gos-tpl-pv-body">{[line('100%', 1, 1), line('90%', 1, 2), line('64%', 1, 3)]}</div>,
    ];
  } else if (layout === 'invite') {
    inner = [
      <div key="h" className="gos-tpl-pv-hero tall" style={{ background: meta.tint, borderColor: c }}><span className="gos-tpl-pv-date" style={{ color: c }} /></div>,
      <div key="t" className="gos-tpl-pv-body center">{[line('64%', 0, 1), line('84%', 1, 2)]}</div>,
      <div key="c" className="gos-tpl-pv-cta" style={{ background: c }} />,
    ];
  } else {
    inner = [
      <div key="b" className="gos-tpl-pv-band sm" style={{ background: c }} />,
      <div key="t" className="gos-tpl-pv-body">{[line('100%', 1, 1), line('92%', 1, 2), line('100%', 1, 3), line('70%', 1, 4), line('48%', 1, 5)]}</div>,
    ];
  }
  return <div className={'gos-tpl-pv' + (compact ? ' compact' : '')}>{inner}</div>;
};

export const TypeChip = ({ type }) => {
  const m = resolveType(type);
  return (
    <span className="gos-tpl-type" style={{ color: m.color, background: m.tint }}>
      <span className="gos-tpl-type-dot" style={{ background: m.color }} />
      {type || 'Basic'}
    </span>
  );
};
export const ModChip = ({ mod }) => (
  <span className="gos-tpl-mod"><Ic name={modIcon(mod)} size={12} />{mod || '—'}</span>
);
export const Avatar = ({ name, size = 24 }) => (
  <span className="gos-ava" style={{ width: size, height: size, fontSize: size * 0.4, background: avaColor(name) }}>{initials(name)}</span>
);
